/**
 * HoverCard — a richer hover surface than a tooltip.
 *
 * Wraps Base UI's `PreviewCard`, which is the hover-intent primitive: it opens
 * on a deliberate hover rather than instantly, and its popup is interactive, so
 * content inside can carry actions a tooltip never could. Styling mirrors
 * `popover.tsx` (same border, surface and motion) so a hover card and a click
 * popover read as the same family of surface.
 *
 * Use a Tooltip for a label. Use this when the hover is meant to preview
 * something — a document, a person, a link target.
 */
import { PreviewCard as PreviewCardPrimitive } from "@base-ui/react/preview-card";

import { cn } from "~/lib/utils";

const HoverCard = PreviewCardPrimitive.Root;

function HoverCardTrigger(props: PreviewCardPrimitive.Trigger.Props) {
  return <PreviewCardPrimitive.Trigger data-slot="hover-card-trigger" {...props} />;
}

function HoverCardPopup({
  className,
  align = "center",
  side = "top",
  sideOffset = 6,
  children,
  ...props
}: PreviewCardPrimitive.Popup.Props & {
  align?: PreviewCardPrimitive.Positioner.Props["align"];
  side?: PreviewCardPrimitive.Positioner.Props["side"];
  sideOffset?: PreviewCardPrimitive.Positioner.Props["sideOffset"];
}) {
  return (
    <PreviewCardPrimitive.Portal>
      <PreviewCardPrimitive.Positioner
        align={align}
        className="z-[140] max-w-(--available-width) transition-[top,left,right,bottom,transform] data-instant:transition-none"
        data-slot="hover-card-positioner"
        side={side}
        sideOffset={sideOffset}
      >
        <PreviewCardPrimitive.Popup
          className={cn(
            "origin-(--transform-origin) rounded-lg border bg-popover text-popover-foreground shadow-lg shadow-black/5 outline-none transition-[scale,opacity] not-dark:bg-clip-padding data-ending-style:scale-98 data-starting-style:scale-98 data-ending-style:opacity-0 data-starting-style:opacity-0 data-instant:duration-0",
            className,
          )}
          data-slot="hover-card-popup"
          {...props}
        >
          {children}
        </PreviewCardPrimitive.Popup>
      </PreviewCardPrimitive.Positioner>
    </PreviewCardPrimitive.Portal>
  );
}

export { HoverCard, HoverCardTrigger, HoverCardPopup };
